import React from 'react';
import PropTypes from 'prop-types';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import AgencyModal from './AgencyModal';
import { useAPI } from 'hooks';
import Locales from 'locales';
import AgencyFormContent from './AgencyFormContent';
import { editAgencySchema } from './agencyFormSchema';

const EditAgencyModal = ({ isOpen, onClose, agency, categories, isReadOnly = false }) => {
  const { api } = useAPI();
  const queryClient = useQueryClient();

  const {
    register,
    handleSubmit,
    control,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: zodResolver(editAgencySchema(categories)),
    defaultValues: {
      name: agency?.name || '',
      ifu: agency?.ifu?.toString() || '',
      phone: agency?.phone?.toString() || '',
      address: agency?.address || '',
      ecobank_user_id: agency?.ecobank_user_id?.toString() || '',
      category_ids: agency?.category_ids?.length ? agency.category_ids : null,
    },
  });

  const { mutate: editAgency, isLoading: isEditing } = useMutation({
    meta: { agloCampaignWrite: true },
    mutationFn: (data) => api.update('agency', data, agency.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['agency/'] });
      queryClient.invalidateQueries({ queryKey: ['trip/'] });
      queryClient.invalidateQueries({ queryKey: ['invoice/'] });
      toast.success(Locales.formatString(Locales.agency.toast.updated_agency, { name: agency.name }));
      onClose();
    },
  });

  const onSubmit = (data) => {
    if (!isReadOnly) editAgency(data);
  };

  return (
    <AgencyModal
      isOpen={isOpen}
      onClose={onClose}
      title={`Éditer une société – ${agency?.name || ''}`}
      description="Modifiez les informations de la société."
      formId="edit-agency-form"
      submitLabel="Enregistrer les modifications"
      savingLabel="Enregistrement…"
      isSaving={isSubmitting || isEditing}
    >
      <form id="edit-agency-form" onSubmit={handleSubmit(onSubmit)} className="min-w-0">
        <AgencyFormContent register={register} errors={errors} control={control} categories={categories} />
      </form>
    </AgencyModal>
  );
};

EditAgencyModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  isReadOnly: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  agency: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    name: PropTypes.string,
    ifu: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    phone: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    address: PropTypes.string,
    ecobank_user_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    admin: PropTypes.shape({
      first_name: PropTypes.string,
      last_name: PropTypes.string,
      email: PropTypes.string,
    }),
  }).isRequired,
  categories: PropTypes.arrayOf(PropTypes.shape({})),
};

export default EditAgencyModal;
